import type React from 'react'
import {useState, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'
import {InputProps, Input} from '../Input/Input'
import {ScrollShadow, ScrollShadowProps} from '../ScrollShadow'
import {captionNormal, spacing, withFormField} from '@webscale-oy/vaalikone-common-ui-base'

type Languages = {[langCode: string]: string}
type KeyOf<T extends object> = Extract<keyof T, string>

export type MultiLangInputProps = {
  activeLang: KeyOf<Languages>
  languages: Languages
  value?: {[key: KeyOf<Languages>]: string | undefined}
  onChange: (language: KeyOf<Languages>, value: string) => void
  onBlur?: (language: KeyOf<Languages>, value?: string) => void
  scrollShadow?: ScrollShadowProps
  /**
   * shows a required symbol next to the main language
   */
  mainLanguage?: string
} & Omit<InputProps, 'onChange' | 'value' | 'onBlur'>

const MultiLangInputElement = ({
  languages,
  activeLang,
  className,
  onChange,
  value = {},
  onBlur,
  scrollShadow,
  mainLanguage,
  ...props
}: MultiLangInputProps) => {
  const {t} = useTranslation()
  const [active, setActive] = useState<KeyOf<Languages>>(activeLang)
  const isMultiLang = useMemo(() => Object.keys(languages).length > 1, [languages])
  return (
    <div className={className}>
      {isMultiLang && (
        <Tabs>
          <ScrollShadow {...scrollShadow}>
            {Object.keys(languages).map(langCode => (
              <Tab
                key={langCode}
                className={active === langCode ? 'active' : ''}
                onClick={() => setActive(langCode)}
                active={active === langCode}
              >
                {languages[langCode]}
                {mainLanguage === langCode && <span aria-label={t('commonUI.generic.required')}>&#42;</span>}
              </Tab>
            ))}
          </ScrollShadow>
        </Tabs>
      )}
      <Input
        {...props}
        value={value[active] ?? ''}
        onChange={val => onChange(active, val)}
        onBlur={val => onBlur && onBlur(active, val)}
        dir={active === 'ara' ? 'rtl' : 'ltr'}
      />
    </div>
  )
}

const Tabs = styled.div`
  display: flex;
  align-items: center;
  overflow-x: hidden;
`
const Tab = styled.button.attrs({
  type: 'button'
})<{active: boolean}>`
  ${captionNormal};
  color: ${({theme, active}) => (active ? theme.text : theme.textSecondary)};
  padding: ${spacing.space_8}px ${spacing.space_16}px;
  border: 1px solid transparent;
  border-radius: ${({theme}) => `${theme.radius} ${theme.radius} 0 0`};
  border-color: ${({theme, active}) => (active ? theme.borderMain : 'transparent')};
  border-bottom-color: ${({theme, active}) => (active ? theme.background : 'transparent')} !important;
  background-color: transparent;
  cursor: pointer;
  z-index: 2;
  transition: all 0.2s ease-in;
`

export const MultiLangInput = styled(MultiLangInputElement)`
  flex: 1;
  ${Input} {
    input {
      border-color: ${({theme}) => theme.borderMain};
      border-radius: ${({theme}) => ` 0 0 ${theme.radius} ${theme.radius}`};
      border-top-color: ${({theme}) => theme.borderMain};
      margin-top: -1px;

      &:focus {
        outline: none;
        box-shadow: none;
      }
    }
  }
  &:focus-within {
    ${Input} {
      input {
        border-color: ${({theme}) => theme.borderPrimary};
        border-top-color: ${({theme}) => theme.borderPrimary};
      }
    }
    ${Tab} {
      &.active {
        border-color: ${({theme}) => theme.borderPrimary};
      }
    }
  }
`

export const FormMultiLangInput = styled(withFormField(MultiLangInput))``
